import {apiGet, apiPost} from "../../shared/api/client";
import {i18n} from "../../shared/i18n";


const REFERRALS_PATH = "/api/v2/admin/referrals";

const cleanParams = (values = {}) => Object.fromEntries(
    Object.entries(values).filter(([, value]) => value !== "" && value !== null && value !== undefined),
);

const scopedConfig = (countryId, values = {}) => ({
    params: cleanParams({...values, country_id: countryId}),
});

const requiredId = (value, label) => {
    const normalized = String(value ?? "").trim();
    if (!/^[1-9]\d*$/.test(normalized)) throw new Error(i18n.t("sections.referrals.p110", {v0: label}));
    return normalized;
};

const credentialsUrl = () => `${REFERRALS_PATH}/credentials`;
const globalAdminConfig = {skipAdminCountryScope: true};

export const referralKeys = {
    all: (countryScope) => ["admin-referrals", String(countryScope ?? "")],
    list: (countryScope, destination, filters, page) => [
        "admin-referrals",
        String(countryScope ?? ""),
        "list",
        destination,
        filters,
        page,
    ],
    properties: (countryScope) => ["admin-referrals", String(countryScope ?? ""), "properties"],
    credentials: () => ["admin-referrals", "global-credentials"],
    detail: (countryScope, refId) => ["admin-referrals", String(countryScope ?? ""), "detail", String(refId)],
    network: (countryScope, refId) => ["admin-referrals", String(countryScope ?? ""), "network", String(refId)],
    placeHistory: (countryScope, range) => ["admin-referrals", String(countryScope ?? ""), "place-history", range],
};

export const listReferrals = ({destination, page = 1, countryId, filters = {}}) => {
    if (!["places", "users"].includes(destination)) throw new Error(i18n.t("sections.referrals.p111"));
    return apiGet(`${REFERRALS_PATH}/${destination}/list`, scopedConfig(countryId, {
        ...filters,
        page,
    }));
};

export const listReferralProperties = ({countryId}) =>
    apiGet("/api/v2/admin/properties/list", scopedConfig(countryId));

export const getReferralCredentials = () => apiGet(credentialsUrl(), globalAdminConfig);

export const updateReferralCredentials = (levels) => apiPost(credentialsUrl(), levels, globalAdminConfig);

export const getReferralDetail = ({refId, countryId}) =>
    apiGet(`${REFERRALS_PATH}/${requiredId(refId, i18n.t("sections.referrals.p112"))}/details`, scopedConfig(countryId));

export const updateReferralDetail = ({refId, refCode, countryId}) =>
    apiPost(
        `${REFERRALS_PATH}/${requiredId(refId, i18n.t("sections.referrals.p112"))}/details`,
        {ref_code: String(refCode ?? "").trim()},
        scopedConfig(countryId),
    );

export const findReferralUser = ({phoneNumber, countryId}) =>
    apiPost(`${REFERRALS_PATH}/find-user`, {phone_number: String(phoneNumber ?? "").trim()}, scopedConfig(countryId));

export const getReferralNetwork = ({refId, countryId}) =>
    apiGet(`${REFERRALS_PATH}/${requiredId(refId, i18n.t("sections.referrals.p112"))}/network`, scopedConfig(countryId));

export const moveReferralUser = ({userId, search, refType, countryId}) =>
    apiPost(
        `${REFERRALS_PATH}/${requiredId(userId, i18n.t("sections.referrals.p113"))}/move`,
        {search: String(search ?? "").trim(), ref_type: refType},
        scopedConfig(countryId),
    );

export const getPlaceReferralHistory = ({range, countryId}) =>
    apiGet(`${REFERRALS_PATH}/place-history`, scopedConfig(countryId, {range}));

export const getDeployerReferrals = () => apiGet("/api/v2/admin/deployer/referrals");

export const fillReferralUser = ({phoneNumber, amount, countryId, idempotencyKey}) => {
    const key = String(idempotencyKey ?? "").trim();
    if (!key) throw new Error(i18n.t("sections.referrals.p114"));
    return apiPost(
        `${REFERRALS_PATH}/fill-user`,
        {phone_number: String(phoneNumber ?? "").trim(), ref_sum: String(amount ?? "").trim()},
        {
            headers: {"Idempotency-Key": key},
            ...scopedConfig(countryId),
        },
    );
};
